import { useCallback } from 'react';

/**
 * Tab history navigation (back/forward within tabs) with URL sync.
 */
export function useHistoryNavigation({
  sprints,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  handleTabClick,
  navigateToCampaign,
  navigateToTemplate,
  navigateToView,
  navigateToNewTab,
  navigateToDocs,
  navigateToDashboards,
  navigateToCampaigns,
  setPreferredDetailView,
  isNavigatingHistoryRef,
}) {
  const handleHistoryNavigate = useCallback(async (historyEntry, previousState) => {
    if (!historyEntry) return;

    isNavigatingHistoryRef.current = true;

    try {
      const { type, targetId, viewMode, customViewId } = historyEntry;

      const isSameContent = previousState &&
        previousState.type === type &&
        previousState.targetId === targetId;

      if (isSameContent) {
        setPreferredDetailView(customViewId
          ? { customViewId, type: viewMode, isHistoryNavigation: true }
          : { type: viewMode, isHistoryNavigation: true });
        if (type === 'campaign') {
          navigateToCampaign(targetId, { viewMode, customViewId });
        } else if (type === 'template') {
          navigateToTemplate(targetId, { viewMode });
        } else if (type === 'view') {
          navigateToView(targetId, { viewMode });
        } else if (type === 'blank') {
          navigateToNewTab();
        } else if (type === 'docs' || type === 'doc') {
          navigateToDocs();
        } else if (type === 'dashboards') {
          navigateToDashboards();
        } else if (type === 'campaigns') {
          navigateToCampaigns();
        }
        return;
      }

      if (type === 'blank') {
        await handleTabClick({ type: 'blank', targetId: null, label: 'New Tab', icon: null });
        navigateToNewTab();
      } else if (type === 'docs' || type === 'doc') {
        await handleTabClick(historyEntry);
        navigateToDocs();
      } else if (type === 'dashboards') {
        await handleTabClick(historyEntry);
        navigateToDashboards();
      } else if (type === 'campaigns') {
        await handleTabClick(historyEntry);
        navigateToCampaigns();
      } else if (type === 'campaign') {
        const campaign = sprints.find(s => s.id === targetId && !s.isTemplate);
        if (campaign) {
          const viewOption = customViewId
            ? { customViewId, type: viewMode, isHistoryNavigation: true }
            : { type: viewMode, isHistoryNavigation: true };
          await handleSelectSprint(campaign, { view: viewOption });
          navigateToCampaign(targetId, { viewMode, customViewId });
        }
      } else if (type === 'template') {
        const template = sprints.find(s => s.id === targetId && (s.isTemplate || s.type === 'template'));
        if (template) {
          await handleSelectTemplate(template);
          navigateToTemplate(targetId, { viewMode });
        }
      } else if (type === 'view') {
        await handleSelectView({ id: targetId });
        navigateToView(targetId, { viewMode });
      }
    } finally {
      setTimeout(() => {
        isNavigatingHistoryRef.current = false;
      }, 100);
    }
  }, [sprints, handleSelectSprint, handleSelectTemplate, handleSelectView, handleTabClick, navigateToCampaign, navigateToTemplate, navigateToView, navigateToNewTab, navigateToDocs, navigateToDashboards, navigateToCampaigns, setPreferredDetailView, isNavigatingHistoryRef]);

  return handleHistoryNavigate;
}
